import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import AddressForm, {
  getAddressError,
  isAddressValid,
  useAddressState,
} from '@/components/AddressForm/AddressForm';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconCheck from '@/icons/Check.svg';
import IconEdit from '@/icons/Edit.svg';
import IconUndo from '@/icons/Undo.svg';
import { AddressRaw, isAddressEmpty } from '@/models/address';
import { Entity, useUpdateEntity } from '@/models/entity';
import { useGeoIpCountry } from '@/services/auth';

import { SubLocationOptions } from '../components/SubLocationOptions';

export const PlaceSection = ({ entity }: { entity: Entity }) => {
  const { t } = useLingui();
  const geoIpCountry = useGeoIpCountry();
  const { mutateAsync: updateEntity, isPending: isUpdateEntityPending } = useUpdateEntity(
    entity.id
  );

  const [address, setAddress] = useAddressState(entity.address ?? { country: geoIpCountry });
  const [logisticsAddress, setLogisticsAddress] = useAddressState(entity.logisticsAddress ?? null);
  const [isEditing, setIsEditing] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const addressError = !isAddressValid(address, true);
  const logisticsAddressError = !isAddressEmpty(logisticsAddress)
    ? !isAddressValid(logisticsAddress, false)
    : false;

  const handleSave = async () => {
    if (addressError || logisticsAddressError) {
      setShowErrors(true);

      return;
    }

    setShowErrors(false);

    await updateEntity({
      address,
      logisticsAddress: isAddressEmpty(logisticsAddress) ? null : (logisticsAddress as AddressRaw),
    });

    setIsEditing(false);
  };

  const discard = () => {
    setIsEditing(false);
    setAddress(entity.address ?? { country: geoIpCountry });
    setLogisticsAddress(entity.logisticsAddress ?? null);
  };

  return (
    <>
      <Box gap="1.5rem">
        <Title as="h2" size="300" weight="medium">
          <Stack row gap="1rem" justifyContent="space-between" alignItems="center">
            <Trans id="settings.entities.place.addresses.title">Addresses</Trans>
            {isEditing ? (
              <Stack row gap="0.5rem">
                <Button
                  ariaLabel={t({
                    id: 'settings.entities.place.addresses.save.label',
                    message: 'Save',
                  })}
                  tooltip={t({
                    id: 'settings.entities.place.addresses.save.label',
                    message: 'Save',
                  })}
                  size="medium"
                  variant="primary"
                  iconOnly
                  onPress={handleSave}
                  isLoading={isUpdateEntityPending}
                >
                  <IconCheck />
                </Button>
                <Button
                  ariaLabel={t({
                    id: 'settings.entities.place.addresses.discard.label',
                    message: 'Discard',
                  })}
                  tooltip={t({
                    id: 'settings.entities.place.addresses.discard.label',
                    message: 'Discard',
                  })}
                  size="medium"
                  variant="secondary-danger"
                  iconOnly
                  onPress={discard}
                  disabled={isUpdateEntityPending}
                >
                  <IconUndo />
                </Button>
              </Stack>
            ) : (
              <Button
                ariaLabel={t({
                  id: 'settings.entities.place.addresses.edit.label',
                  message: 'Edit location',
                })}
                tooltip={t({
                  id: 'settings.entities.place.addresses.edit.label',
                  message: 'Edit location',
                })}
                size="medium"
                variant="secondary"
                iconOnly
                onPress={() => setIsEditing(true)}
              >
                <IconEdit />
              </Button>
            )}
          </Stack>
        </Title>
        <AddressForm
          label={
            <Trans id="settings.entities.place.addresses.main-address.label">Main address</Trans>
          }
          value={address}
          onChange={setAddress}
          contact={true}
          isDisabled={!isEditing || isUpdateEntityPending}
          error={showErrors && addressError ? getAddressError(address, true) : undefined}
        />

        <AddressForm
          label={
            <Trans id="settings.entities.place.addresses.logistics-address.label">
              Logistics address (optional)
            </Trans>
          }
          value={logisticsAddress}
          onChange={setLogisticsAddress}
          contact={false}
          isDisabled={!isEditing || isUpdateEntityPending}
          error={
            showErrors && logisticsAddressError
              ? getAddressError(logisticsAddress, false)
              : undefined
          }
        />

        <Message type="info">
          <Trans id="settings.entities.place.addresses.shipping-address-hint">
            Shipments use the logistics address when set, otherwise the main address.
          </Trans>
        </Message>
      </Box>
      <SubLocationOptions entityId={entity.id} />
    </>
  );
};
